import React, { useState, useEffect } from 'react';
import { parseModbusData } from '../utils/modbusDecoder';
import { extractRecordModbus, withAnalysisFlag } from '../utils/telemetrySource';
import { BATTERY_TYPE } from '../utils/assetConfig';
import { API_BASE } from '../utils/api';
import * as XLSX from 'xlsx';
import './HistoricData.css';

function ModuleHistoricData({ authData, selectedDevice, assetConfig, batterySlaveIds, groupDevices }) {
  // Build device list: use groupDevices if available (for selected group),
  // otherwise fall back to authData.devices, then selectedDevice
  const deviceList = groupDevices?.length > 0
    ? groupDevices
    : authData?.devices?.length > 0
      ? authData.devices
      : selectedDevice ? [selectedDevice] : [];

  const isDaly = assetConfig?.type === BATTERY_TYPE.DALYLV;
  const [localSelectedDevice, setLocalSelectedDevice] = useState(selectedDevice?.deviceId || '');
  const [selectedBatteryIndex, setSelectedBatteryIndex] = useState(0);

  // Set default times: endTime = now, startTime = 6 hours earlier
  const getDefaultTimes = () => {
    const now = new Date();
    const sixHoursAgo = new Date(now.getTime() - (6 * 60 * 60 * 1000));

    // Format to local datetime string (YYYY-MM-DDTHH:MM)
    const formatLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return {
      start: formatLocal(sixHoursAgo),
      end: formatLocal(now)
    };
  };

  const defaultTimes = getDefaultTimes();
  const [startTime, setStartTime] = useState(defaultTimes.start);
  const [endTime, setEndTime] = useState(defaultTimes.end);
  const [moduleNumber, setModuleNumber] = useState('1');
  const [historicData, setHistoricData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Convert local datetime to ISO string for API
  const toISOString = (localDateTime) => {
    const date = new Date(localDateTime);
    return date.toISOString();
  };

  const fetchData = async () => {
    if (!authData || !authData.token) {
      setError('Not authenticated. Please login again.');
      return;
    }

    if (!localSelectedDevice) {
      setError('Please select a device.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startDate = new Date(startTime);
      const endDate = new Date(endTime);
      const timeDiffHours = (endDate - startDate) / (1000 * 60 * 60);

      let allProcessedData = [];

      // If interval > 24 hours, split into chunks
      if (timeDiffHours > 24) {
        const chunks = [];
        let currentStart = new Date(startDate);

        while (currentStart < endDate) {
          const currentEnd = new Date(currentStart.getTime() + (24 * 60 * 60 * 1000));
          const chunkEnd = currentEnd > endDate ? endDate : currentEnd;

          chunks.push({
            start: currentStart.toISOString(),
            end: chunkEnd.toISOString()
          });

          currentStart = chunkEnd;
        }

        // Fetch data for each chunk
        for (const chunk of chunks) {
          const rawUrl = `${API_BASE}/api/getLocationInterval?api_access_token=${authData.token}&deviceId=${localSelectedDevice}&startDate=${chunk.start}&endDate=${chunk.end}`;
          const url = withAnalysisFlag(rawUrl);

          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          let processedData = [];
          if (data && data.locationInfo && Array.isArray(data.locationInfo)) {
            processedData = data.locationInfo;
          } else if (Array.isArray(data)) {
            processedData = data;
          }

          allProcessedData = [...allProcessedData, ...processedData];
        }
      } else {
        // Single API call for intervals <= 24 hours
        const startTimeISO = toISOString(startTime);
        const endTimeISO = toISOString(endTime);

        const rawUrl = `${API_BASE}/api/getLocationInterval?api_access_token=${authData.token}&deviceId=${localSelectedDevice}&startDate=${startTimeISO}&endDate=${endTimeISO}`;
        const url = withAnalysisFlag(rawUrl);

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data && data.locationInfo && Array.isArray(data.locationInfo)) {
          allProcessedData = data.locationInfo;
        } else if (Array.isArray(data)) {
          allProcessedData = data;
        }
      }

      const moduleNum = parseInt(moduleNumber) || 1;
      const cellBase = isDaly ? 0x00 : 0x2000 + ((moduleNum - 1) * 32);
      const tempBase = isDaly ? 0x30 : cellBase + 24;
      const defaultCellCount = isDaly ? 48 : 24;
      const defaultTempCount = 8;

      // Process each location record
      const tableData = allProcessedData.map(item => {
        const row = {
          timestamp: item.timestamp,
          latitude: item.latitude,
          longitude: item.longitude,
          speed: item.speed,
          cellVoltages: [],
          temperatures: []
        };

        // Parse modbus data if available
        let modbusArray = [];
        const slaveId = batterySlaveIds ? batterySlaveIds[selectedBatteryIndex] : null;
        const rs485modbus = extractRecordModbus(item);
        if (rs485modbus) {
          modbusArray = parseModbusData(rs485modbus, slaveId, assetConfig?.type);
        }

        let cellCount = defaultCellCount;
        let tempCount = defaultTempCount;

        if (isDaly && modbusArray.length > 0) {
          const regCell = modbusArray.find(reg => reg.address === 0x3C);
          if (regCell && !isNaN(Number(regCell.parsedValue)) && Number(regCell.parsedValue) > 0) {
            cellCount = Number(regCell.parsedValue);
          }
          const regTemp = modbusArray.find(reg => reg.address === 0x3D);
          if (regTemp && !isNaN(Number(regTemp.parsedValue)) && Number(regTemp.parsedValue) > 0) {
            tempCount = Number(regTemp.parsedValue);
          }
        }

        // Extract cell voltages
        for (let i = 0; i < cellCount; i++) {
          const cellAddress = cellBase + i;
          const found = modbusArray.find(reg => reg.address === cellAddress);
          if (found) {
            row.cellVoltages.push({
              cell: i + 1,
              value: found.parsedValue,
              unit: found.unit
            });
          }
        }

        // Extract temperatures
        for (let i = 0; i < tempCount; i++) {
          const tempAddress = tempBase + i;
          const found = modbusArray.find(reg => reg.address === tempAddress);
          if (found) {
            row.temperatures.push({
              sensor: i + 1,
              value: found.parsedValue,
              unit: found.unit
            });
          }
        }

        return row;
      });

      setHistoricData(tableData);

    } catch (err) {
      setError(err.message);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    let sortableData = [...historicData];
    if (sortConfig.key !== null) {
      sortableData.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'timestamp') {
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
        } else if (sortConfig.key === 'latitude' || sortConfig.key === 'longitude' || sortConfig.key === 'speed') {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        } else if (sortConfig.key.startsWith('cell')) {
          // Sort by cell voltage
          const cellIndex = parseInt(sortConfig.key.replace('cell', '')) - 1;
          aValue = parseFloat(a.cellVoltages[cellIndex]?.value) || 0;
          bValue = parseFloat(b.cellVoltages[cellIndex]?.value) || 0;
        } else if (sortConfig.key.startsWith('temp')) {
          // Sort by temperature
          const tempIndex = parseInt(sortConfig.key.replace('temp', '')) - 1;
          aValue = parseFloat(a.temperatures[tempIndex]?.value) || 0;
          bValue = parseFloat(b.temperatures[tempIndex]?.value) || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [historicData, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // Export to Excel
  const exportToExcel = () => {
    const worksheetData = sortedData.map(row => {
      const exportRow = {
        'Timestamp': formatDateTime(row.timestamp),
        'Latitude': row.latitude,
        'Longitude': row.longitude,
        'Speed (km/h)': row.speed
      };

      // Add cell voltages
      row.cellVoltages.forEach(cell => {
        exportRow[`Cell ${cell.cell} Voltage`] = `${cell.value} ${cell.unit}`.trim();
      });

      // Add temperatures
      row.temperatures.forEach(temp => {
        exportRow[`Temp Sensor ${temp.sensor}`] = `${temp.value} ${temp.unit}`.trim();
      });

      return exportRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    const sheetName = isDaly ? 'Cell Voltages' : `Module ${moduleNumber}`;
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate filename with timestamp
    const now = new Date();
    const filename = `${isDaly ? 'Cell' : `Module${moduleNumber}`}_Data_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  // Reset page when data changes or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [historicData, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = sortedData.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="historic-data-view">
      <h2>{isDaly ? 'Cell Historic Data' : 'Module Historic Data'}</h2>
      <p className="subtitle">
        {isDaly
          ? 'View individual cell voltages and temperatures over time'
          : 'View individual module cell voltages and temperatures over time'}
      </p>

      <form onSubmit={handleSubmit} className="input-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="device">Select Vehicle:</label>
            <select
              id="device"
              value={localSelectedDevice}
              onChange={(e) => setLocalSelectedDevice(e.target.value)}
              required
            >
              <option value="">-- Select a vehicle --</option>
              {deviceList.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.vehicleId} ({device.deviceId})
                </option>
              ))}
            </select>
          </div>

          {batterySlaveIds && batterySlaveIds.length > 1 && (
            <div className="form-group">
              <label htmlFor="battery">Battery:</label>
              <select
                id="battery"
                value={selectedBatteryIndex}
                onChange={(e) => setSelectedBatteryIndex(parseInt(e.target.value))}
              >
                {batterySlaveIds.map((slaveId, index) => (
                  <option key={slaveId} value={index}>
                    Battery {index + 1} (Slave {slaveId})
                  </option>
                ))}
              </select>
            </div>
          )}

          {(!assetConfig || assetConfig.modCount > 0) && (
            <div className="form-group">
              <label htmlFor="moduleNumber">
                Module Number (1-{assetConfig?.modCount || 1}):
              </label>
              <input
                type="number"
                id="moduleNumber"
                min="1"
                max={assetConfig?.modCount || 9}
                value={moduleNumber}
                onChange={(e) => setModuleNumber(e.target.value)}
                required
              />
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="startTime">Start Time (Local):</label>
            <input
              type="datetime-local"
              id="startTime"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="endTime">End Time (Local):</label>
            <input
              type="datetime-local"
              id="endTime"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
            />
          </div>
        </div>

        <button type="submit" disabled={loading}>
          {loading ? 'Loading...' : 'Fetch Data'}
        </button>
      </form>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {historicData.length > 0 && (
        <div className="results">
          <div className="results-header">
            <h3>{isDaly ? 'Cell Voltages' : `Module ${moduleNumber} Data`} ({historicData.length} records)</h3>
            <button onClick={exportToExcel} className="export-button">
              Export to Excel
            </button>
          </div>

          {/* Cell Voltages Table */}
          {historicData[0].cellVoltages.length > 0 && (
            <>
              <h4>Cell Voltages</h4>
              <div className="table-container">
                <table className="historic-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('timestamp')} style={{ cursor: 'pointer' }}>
                        Timestamp{getSortIndicator('timestamp')}
                      </th>
                      {historicData[0].cellVoltages.map(cell => (
                        <th key={cell.cell} onClick={() => handleSort(`cell${cell.cell}`)} style={{ cursor: 'pointer' }}>
                          Cell {cell.cell}{getSortIndicator(`cell${cell.cell}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((row, index) => (
                      <tr key={startIndex + index}>
                        <td className="timestamp-cell">{formatDateTime(row.timestamp)}</td>
                        {row.cellVoltages.map((cell, idx) => (
                          <td key={idx}>
                            {cell.value} {cell.unit}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Temperatures Table */}
          {historicData[0].temperatures.length > 0 && (
            <>
              <h4 style={{ marginTop: '2rem' }}>Temperatures</h4>
              <div className="table-container">
                <table className="historic-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('timestamp')} style={{ cursor: 'pointer' }}>
                        Timestamp{getSortIndicator('timestamp')}
                      </th>
                      {historicData[0].temperatures.map(temp => (
                        <th key={temp.sensor} onClick={() => handleSort(`temp${temp.sensor}`)} style={{ cursor: 'pointer' }}>
                          Temp {temp.sensor}{getSortIndicator(`temp${temp.sensor}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((row, index) => (
                      <tr key={startIndex + index}>
                        <td className="timestamp-cell">{formatDateTime(row.timestamp)}</td>
                        {row.temperatures.map((temp, idx) => (
                          <td key={idx}>
                            {temp.value} {temp.unit}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pagination Controls - Shared for both tables if they share the same data source */}
          <div className="pagination-controls">
            <div className="rows-per-page">
              <label htmlFor="rowsPerPage">Rows per page:</label>
              <select id="rowsPerPage" value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={sortedData.length}>All</option>
              </select>
            </div>
            <div className="page-info">
              {startIndex + 1}-{Math.min(startIndex + rowsPerPage, sortedData.length)} of {sortedData.length}
            </div>
            <div className="page-nav">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="nav-button"
              >
                Previous
              </button>
              <span className="page-number">Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="nav-button"
              >
                Next
              </button>
            </div>
          </div>

          {historicData[0].cellVoltages.length === 0 && historicData[0].temperatures.length === 0 && (
            <div className="no-data">
              <p>No module data found in the response. The data may not contain module-level information.</p>
            </div>
          )}
        </div>
      )}

      {!loading && historicData.length === 0 && !error && (
        <div className="no-data">
          <p>No data loaded. Enter device information and click "Fetch Data".</p>
        </div>
      )}
    </div>
  );
}

export default ModuleHistoricData;

